import { Pencil, Plus } from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent } from '@/components/ui/card'

import { ProfileStandingSectionHeader } from './profile-standing-section-header'

import {
  candidateSections,
  type Experience,
  type Education,
  type Certification,
  type ProfilePaper,
  type ProfileData,
} from '@/hooks/use-profile'
import { formatProfileCertificationDates, formatProfilePaperMeta } from '@/lib/profile-evidence-display'

import { ExperienceEvidenceCard } from './experience-evidence-card'
import {
  profileSectionEmptyCtaClass,
  profileSectionItemClass,
  profileSectionItemEditButtonClass,
  profileSectionItemListClass,
  profileSectionManageButtonClass,
} from '@/lib/profile-section-styles'
import { cn } from '@/lib/utils'

function ProfileSectionHeaderAddAction({
  onClick,
  disabled,
  ariaLabel,
  title,
}: {
  onClick: () => void
  disabled?: boolean
  ariaLabel: string
  title: string
}) {
  return (
    <CardAction className="row-span-1 self-center">
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className={profileSectionManageButtonClass}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        title={title}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </CardAction>
  )
}

function ProfileSectionItemRow({
  children,
  highlight,
  onEdit,
  editDisabled,
  editAriaLabel,
  editTitle,
}: {
  children: ReactNode
  highlight?: string
  onEdit?: () => void
  editDisabled?: boolean
  editAriaLabel: string
  editTitle: string
}) {
  return (
    <div
      className={cn(profileSectionItemClass, 'relative')}
      {...(highlight ? { 'data-agent-highlight': highlight } : {})}
    >
      <div className="min-w-0">{children}</div>
      {onEdit ? (
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          className={profileSectionItemEditButtonClass}
          onClick={onEdit}
          disabled={editDisabled}
          aria-label={editAriaLabel}
          title={editTitle}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  )
}

export function ProfileExperienceSection({
  profile,
  onOpenExperience,
  readOnly = false,
}: {
  profile: ProfileData | null | undefined
  onOpenExperience?: (experience: Experience | null) => void
  readOnly?: boolean
}) {
  const experiences = candidateSections(profile).experiences || []
  const hasExperiences = experiences.length > 0

  if (readOnly && !hasExperiences) {
    return null
  }

  return (
    <Card className="group/section">
      <ProfileStandingSectionHeader
        title="Experience"
        description={!readOnly ? 'Each role is managed independently in its own canvas.' : null}
        action={
          !readOnly && hasExperiences ? (
            <ProfileSectionHeaderAddAction
              onClick={() => onOpenExperience?.(null)}
              disabled={!profile}
              ariaLabel="Add experience"
              title="Add experience"
            />
          ) : undefined
        }
      />
      <CardContent className={cn(profileSectionItemListClass, 'pt-0')}>
        {!readOnly && !hasExperiences ? (
          <Button
            type="button"
            variant="outline"
            className={profileSectionEmptyCtaClass}
            onClick={() => onOpenExperience?.(null)}
            disabled={!profile}
          >
            <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            Add your first experience
          </Button>
        ) : null}
        {experiences.map((exp) => (
          <ProfileSectionItemRow
            key={exp.id}
            highlight={
              profile?.id && exp.id ? `profile:${profile.id}:experience:${exp.id}` : undefined
            }
            onEdit={!readOnly ? () => onOpenExperience?.(exp) : undefined}
            editAriaLabel={`Edit ${exp.title} at ${exp.company}`}
            editTitle="Edit experience"
          >
            <div className="space-y-1">
              <ExperienceEvidenceCard experience={exp} variant="full" />
              {(exp.skills || []).length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {(exp.skills || []).map((skill) => (
                    <Badge key={skill} variant="outline" className="px-2 py-0 text-xs">{skill}</Badge>
                  ))}
                </div>
              ) : null}
            </div>
          </ProfileSectionItemRow>
        ))}
      </CardContent>
    </Card>
  )
}

export function ProfilePapersSection({
  profile,
  papersHighlighted = false,
  onOpenPaper,
  readOnly = false,
}: {
  profile: ProfileData | null | undefined
  papersHighlighted?: boolean
  onOpenPaper?: (paper: ProfilePaper | null) => void
  readOnly?: boolean
}) {
  const papers = candidateSections(profile).papers || []
  const hasPapers = papers.length > 0

  if (readOnly && !hasPapers) {
    return null
  }

  return (
    <Card
      className={cn(
        'group/section transition-all duration-300',
        papersHighlighted ? 'bg-success/5 ring-2 ring-success/50' : '',
      )}
    >
      <ProfileStandingSectionHeader
        title="Publications"
        description={!readOnly ? 'Each paper is managed independently in its own canvas.' : null}
        action={
          !readOnly && hasPapers ? (
            <ProfileSectionHeaderAddAction
              onClick={() => onOpenPaper?.(null)}
              disabled={!profile}
              ariaLabel="Add a publication"
              title="Add a publication"
            />
          ) : undefined
        }
      />
      <CardContent className={cn(profileSectionItemListClass, 'pt-0')}>
        {papers.map((item, index) => {
          const meta = formatProfilePaperMeta(item)
          return (
            <ProfileSectionItemRow
              key={item.id || `${item.title}-${index}`}
              highlight={
                profile?.id && item.id ? `profile:${profile.id}:paper:${item.id}` : undefined
              }
              onEdit={!readOnly ? () => onOpenPaper?.(item) : undefined}
              editDisabled={!item.id}
              editAriaLabel={`Edit ${item.title}`}
              editTitle="Edit publication"
            >
              <div className="space-y-1">
                <p className="font-medium text-foreground">{item.title}</p>
                {meta ? <p className="text-xs text-muted-foreground">{meta}</p> : null}
                {item.description ? (
                  <p className="mt-1 text-base text-muted-foreground">{item.description}</p>
                ) : null}
                {item.url ? (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block text-sm text-primary underline-offset-2 hover:underline"
                  >
                    Open paper
                  </a>
                ) : null}
              </div>
            </ProfileSectionItemRow>
          )
        })}
        {!readOnly && !hasPapers ? (
          <Button
            type="button"
            variant="outline"
            className={profileSectionEmptyCtaClass}
            onClick={() => onOpenPaper?.(null)}
            disabled={!profile}
          >
            <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            Add a publication
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function ProfileEducationSection({
  profile,
  educationHighlighted,
  formatDateRange,
  onOpenEducation,
  readOnly = false,
}: {
  profile: ProfileData | null | undefined
  educationHighlighted: boolean
  formatDateRange: (start?: string, end?: string | null) => string
  onOpenEducation?: (education: Education | null) => void
  readOnly?: boolean
}) {
  const education = candidateSections(profile).education || []
  const hasEducation = education.length > 0

  if (readOnly && !hasEducation) {
    return null
  }

  return (
    <Card
      className={cn(
        'group/section transition-all duration-300',
        educationHighlighted ? 'bg-success/5 ring-2 ring-success/50' : '',
      )}
    >
      <ProfileStandingSectionHeader
        title="Education"
        description={!readOnly ? 'Each programme is managed independently in its own canvas.' : null}
        action={
          !readOnly && hasEducation ? (
            <ProfileSectionHeaderAddAction
              onClick={() => onOpenEducation?.(null)}
              disabled={!profile}
              ariaLabel="Add education"
              title="Add education"
            />
          ) : undefined
        }
      />
      <CardContent className={cn(profileSectionItemListClass, 'pt-0')}>
        {education.map((item, index) => (
          <ProfileSectionItemRow
            key={item.id || `${item.institution}-${item.degree}-${index}`}
            highlight={
              profile?.id && item.id ? `profile:${profile.id}:education:${item.id}` : undefined
            }
            onEdit={!readOnly ? () => onOpenEducation?.(item) : undefined}
            editDisabled={!item.id}
            editAriaLabel={`Edit ${item.degree} at ${item.institution}`}
            editTitle="Edit education"
          >
            <div className="space-y-1">
              <p className="font-medium">{item.degree} <span className="text-muted-foreground">@ {item.institution}</span></p>
              {(item.field_of_study || item.start_date || item.end_date) && (
                <p className="text-xs text-muted-foreground">
                  {[item.field_of_study, formatDateRange(item.start_date || undefined, item.end_date)].filter(Boolean).join(' • ')}
                </p>
              )}
              {item.description ? <p className="mt-1 text-base text-muted-foreground">{item.description}</p> : null}
              {(item.related_skills || []).length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {(item.related_skills || []).map((skill) => (
                    <Badge key={skill} variant="outline" className="px-2 py-0 text-xs">{skill}</Badge>
                  ))}
                </div>
              ) : null}
            </div>
          </ProfileSectionItemRow>
        ))}
        {!readOnly && !hasEducation ? (
          <Button
            type="button"
            variant="outline"
            className={profileSectionEmptyCtaClass}
            onClick={() => onOpenEducation?.(null)}
            disabled={!profile}
          >
            <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            Add your first education entry
          </Button>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function ProfileCertificationsSection({
  profile,
  certificationsHighlighted,
  onOpenCertification,
  readOnly = false,
}: {
  profile: ProfileData | null | undefined
  certificationsHighlighted: boolean
  onOpenCertification?: (certification: Certification | null) => void
  readOnly?: boolean
}) {
  const certifications = candidateSections(profile).certifications || []
  const hasCertifications = certifications.length > 0

  if (readOnly && !hasCertifications) {
    return null
  }

  return (
    <Card
      className={cn(
        'group/section transition-all duration-300',
        certificationsHighlighted ? 'bg-success/5 ring-2 ring-success/50' : '',
      )}
    >
      <ProfileStandingSectionHeader
        title="Certifications"
        description={!readOnly ? 'Each certification is managed independently in its own canvas.' : null}
        action={
          !readOnly && hasCertifications ? (
            <ProfileSectionHeaderAddAction
              onClick={() => onOpenCertification?.(null)}
              disabled={!profile}
              ariaLabel="Add certification"
              title="Add certification"
            />
          ) : undefined
        }
      />
      <CardContent className={cn(profileSectionItemListClass, 'pt-0')}>
        {!readOnly && !hasCertifications ? (
          <Button
            type="button"
            variant="outline"
            className={profileSectionEmptyCtaClass}
            onClick={() => onOpenCertification?.(null)}
            disabled={!profile}
          >
            <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            Add your first certification
          </Button>
        ) : null}
        {certifications.map((item, index) => {
          const certDates = formatProfileCertificationDates(item.issued_at, item.expires_at)
          return (
            <ProfileSectionItemRow
              key={item.id || `${item.name}-${item.issuer}-${index}`}
              highlight={
                profile?.id && item.id
                  ? `profile:${profile.id}:certification:${item.id}`
                  : undefined
              }
              onEdit={!readOnly ? () => onOpenCertification?.(item) : undefined}
              editDisabled={!item.id}
              editAriaLabel={`Edit ${item.name} from ${item.issuer}`}
              editTitle="Edit certification"
            >
              <div className="space-y-1">
                <p className="font-medium">{item.name} <span className="text-muted-foreground">@ {item.issuer}</span></p>
                {certDates ? (
                  <p className="text-xs text-muted-foreground">{certDates}</p>
                ) : null}
                {item.description ? <p className="mt-1 text-base text-muted-foreground">{item.description}</p> : null}
                {(item.related_skills || []).length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {(item.related_skills || []).map((skill) => (
                      <Badge key={skill} variant="outline" className="px-2 py-0 text-xs">{skill}</Badge>
                    ))}
                  </div>
                ) : null}
              </div>
            </ProfileSectionItemRow>
          )
        })}
      </CardContent>
    </Card>
  )
}
